import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Text,
  Textarea,
} from '@chakra-ui/react';

import { PointsTransactionReasonLabels } from 'app/constants/pointsTransactions';

import {
  Controller,
  UseFormRegister,
  FieldErrors,
  UseFormWatch,
  Control,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form';

import { DropdownInput, shadowStyles, textStyles } from '@aslanpay/portal-common-lib';

import { formatCurrency } from 'app/utils/strings';

import { useEffect, useRef } from 'react';

import SendTimingFields from 'app/components/Gifting/SendTimingFields';
import { SCHEDULED_GIFT_REASON } from 'app/utils/scheduledGifts';

import { getFilteredPointsTransactionReasonOptions } from 'app/utils/gifting';

import formatNumber from 'app/utils/formatting';

import getPointsValue from 'app/utils/points';

import { RewardFormFields } from './types';

interface RewardDetailsFormProps {
  register: UseFormRegister<RewardFormFields>;
  errors: FieldErrors<RewardFormFields>;
  selectedEmployeeCount: number;
  onStepAction: () => void;
  currentPoints: number;
  watch: UseFormWatch<RewardFormFields>;
  control: Control<RewardFormFields>;
  setValue: UseFormSetValue<RewardFormFields>;
  trigger: UseFormTrigger<RewardFormFields>;
  /** The company has gifting turned on, so a gift can be scheduled for later. */
  canSchedule?: boolean;
  /** The company has Reward & Recognition turned on, so a scheduled gift can also be posted as a recognition. */
  canPostAsRecognition?: boolean;
  /** Editing a scheduled gift: it can only be rescheduled, and its reason can't change. */
  isEditing?: boolean;
}

function RewardDetailsForm({
  register,
  errors,
  selectedEmployeeCount,
  currentPoints,
  watch,
  control,
  setValue,
  trigger,
  canSchedule = false,
  canPostAsRecognition = false,
  isEditing = false,
}: RewardDetailsFormProps) {
  const pointValue = getPointsValue(currentPoints);

  const watchPointsAmount = watch('pointsAmount');
  const customMessageValue = watch('customMessage') || '';
  const watchReason = watch('transactionReason') as keyof typeof PointsTransactionReasonLabels;

  const watchSendMode = watch('sendMode');
  const watchScheduleDate = watch('scheduleDate') || '';
  const watchScheduleTime = watch('scheduleTime') || '';

  const pointsTransactionReasonOptions = getFilteredPointsTransactionReasonOptions();

  // Fill in the reason's default text only when the reason is changed here, so text already filled in (a gift
  // being edited or sent again, or coming back from the next step) isn't overwritten when the step opens.
  const shownReason = useRef(watchReason);
  useEffect(() => {
    if (watchReason && watchReason !== shownReason.current) {
      setValue('customMessage', PointsTransactionReasonLabels[watchReason]?.notes ?? '');
    }
    shownReason.current = watchReason;
  }, [setValue, watchReason]);

  // A scheduled gift is always an employee gift; sending now keeps every reason.
  const isReasonLocked = isEditing || watchSendMode === 'SCHEDULE';
  useEffect(() => {
    if (isReasonLocked && watchReason !== SCHEDULED_GIFT_REASON) {
      // The notification text stays as the admin left it.
      shownReason.current = SCHEDULED_GIFT_REASON;
      setValue('transactionReason', SCHEDULED_GIFT_REASON, { shouldValidate: true });
    }
  }, [isReasonLocked, watchReason, setValue]);

  useEffect(() => {
    if (customMessageValue) {
      trigger('customMessage');
    }
  }, [customMessageValue, trigger]);

  return (
    <Flex
      flexDir="column"
      maxW="572px"
      alignSelf="center"
      gap="30px"
      borderRadius="10px"
      boxShadow={shadowStyles.dropdownMenu}
      minW="480px"
    >
      <HStack
        minH="65px"
        px="20px"
        py="20px"
        borderBottom="1px solid"
        borderColor="brand.lightGray1"
        alignItems="center"
        justify="space-between"
      >
        <FormLabel as="h5" sx={{ ...textStyles.h5Bold }} margin="0">
          Reward Points
        </FormLabel>
      </HStack>

      <HStack mx="20px" spacing={10} justify="start">
        <Box>
          <Text color="brand.darkGray" textStyle="extraSmallSemiBold" mb="3px">
            Current points balance
          </Text>
          <Text color="brand.purple" textStyle="h6SemiBold">
            {formatNumber(currentPoints)}
          </Text>
        </Box>
        <Box>
          <Text color="brand.darkGray" textStyle="extraSmallSemiBold" mb="3px">
            Value in points
          </Text>
          <Text color="brand.purple" textStyle="h6SemiBold">
            {formatCurrency(pointValue, 'GBP')}
          </Text>
        </Box>
      </HStack>

      <Box px="20px" pb="20px">
        <FormControl pb="20px" isInvalid={!!errors.pointsAmount} isRequired>
          <FormLabel>Reward Points per Employee</FormLabel>
          <Input type="number" {...register('pointsAmount')} />
          <FormErrorMessage>{errors.pointsAmount?.message}</FormErrorMessage>
          {watchPointsAmount !== undefined && (
            <Text mt="6px" textStyle="extraSmallMedium">
              Total points to award:{' '}
              {formatNumber(watchPointsAmount * selectedEmployeeCount, 'en-GB', 0, 2)}
            </Text>
          )}
          {/* {watchPointsAmount !== undefined && watchPointsAmount > currentPoints && (
            <Text textStyle="extraSmallMedium" color="red.500">
              You do not have enough points to reward this amount.
            </Text>
          )} */}
        </FormControl>

        <FormControl pb="20px" isInvalid={!!errors?.transactionReason?.message} isRequired>
          <FormLabel>Reason</FormLabel>
          <Controller
            control={control}
            name="transactionReason"
            render={({ field: { onChange, onBlur, value } }) => (
              <DropdownInput
                // The dropdown only reads its value when it mounts, so it's remounted when the value is set here.
                key={`${isReasonLocked}-${value}`}
                isDisabled={isReasonLocked}
                placeholder="Select a transaction reason"
                defaultValue={pointsTransactionReasonOptions.find(
                  (transactionReason) => transactionReason.value === value
                )}
                options={pointsTransactionReasonOptions}
                onChange={onChange}
                onBlur={onBlur}
              />
            )}
          />
          {isReasonLocked && (
            <FormHelperText>Scheduled gifts are always sent as an Employee gift.</FormHelperText>
          )}
          <FormErrorMessage>{errors?.transactionReason?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.customMessage} isRequired>
          <FormLabel>Notification reason (max 130 characters)</FormLabel>
          <Box position="relative">
            <Textarea maxLength={130} rows={4} {...register('customMessage')} pr="60px" />
            <Text position="absolute" bottom="8px" right="12px" fontSize="sm" color="gray.400">
              {customMessageValue.length}/130
            </Text>
          </Box>
          <FormErrorMessage>{errors.customMessage?.message}</FormErrorMessage>
        </FormControl>

        {(canSchedule || isEditing) && (
          <Box pt="30px" data-testid="send-timing-card">
            <Text as="h6" textStyle="h6SemiBold" color="brand.darkPurple" mb="16px">
              When to send
            </Text>
            <SendTimingFields
              sendMode={watchSendMode}
              onSendModeChange={(mode) => {
                setValue('sendMode', mode, { shouldValidate: true });
                // The balance check only applies to sending now, so re-check the points.
                trigger('pointsAmount');
              }}
              scheduleDate={watchScheduleDate}
              onScheduleDateChange={(date) => {
                setValue('scheduleDate', date, { shouldValidate: true });
                // A time still to come on one day may already have gone on another.
                setValue('scheduleTime', watchScheduleTime, { shouldValidate: true });
              }}
              scheduleTime={watchScheduleTime}
              onScheduleTimeChange={(time) =>
                setValue('scheduleTime', time, { shouldValidate: true })
              }
              dateError={errors.scheduleDate?.message}
              timeError={errors.scheduleTime?.message}
              canPostAsRecognition={canPostAsRecognition}
              postAsRecognition={!!watch('postAsRecognition')}
              onPostAsRecognitionChange={(value) => setValue('postAsRecognition', value)}
              isScheduleOnly={isEditing}
            />
          </Box>
        )}
      </Box>
    </Flex>
  );
}

export default RewardDetailsForm;
